import React from "react"
import { Grid, List, ListItem, Typography } from "@material-ui/core"
import Facebook from "../../../components/svgs/Facebook.svg"
import LinkedIn from "../../../components/svgs/LinkedIN.svg"
import Medium from "../../../components/svgs/Medium.svg"
import Youtube from "../../../components/svgs/Youtube.svg"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import { FormattedMessage } from "react-intl"
import useScrollImpressionLog from "../../../hooks/useScrollImpressionLog"
import usePlatform from "../../../hooks/usePlatform"

import getDocsUrl from "../../../fixtures/url"
import { Locale } from "../../../locale"
import { Link } from "react-router-dom"
import { useTrack, useVariation } from "@hackler/react-sdk"
import { ADD_DATA_ANALYTICS_DETAIL_PAGE } from "../../../abTestKey"

interface StyleProps {
  lang: string
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) =>
  createStyles({
    root: {
      padding: "40px 0",
      background: "#FFF",
      [theme.breakpoints.up("md")]: {
        padding: theme.spacing(10, 15),
      },
    },
    container: {
      maxWidth: "1200px",
      width: "100%",
      margin: "0 auto",
    },
    footerDivider: {
      display: "none",
      [theme.breakpoints.up("md")]: {
        display: "block",
        color: "#EDEDED",
      },
    },
    footerAlpha6Text: {
      color: "rgba(0,0,0,0.6)",
      textDecoration: "none",
      fontSize: "16px",
    },
    footerAlpha3Text: (props) => ({
      color: "rgba(0,0,0,0.3)",
      fontSize: props.lang === "ko" ? "12px" : "16px",
      "& a": {
        color: "rgba(0,0,0,0.3)",
        textDecoration: "none",
      },
    }),
    h6: {
      fontSize: "20px",
    },
  })
)

interface Props {
  pageName?: string
}

export default function Footer({ pageName = "home" }: Props) {
  const track = useTrack()
  const { lang } = Locale.getDefault()
  const classes = useStyles({ lang })
  const { isDesktop, isMobile } = usePlatform()
  const { ref } = useScrollImpressionLog({ message: "8: 푸터", eventKey: `homepage_${pageName}_footer_impression` })
  const shownDataAnalyticsMenu = useVariation(ADD_DATA_ANALYTICS_DETAIL_PAGE) === "B"

  return (
    <div className={classes.root} ref={ref}>
      <Grid container spacing={5} justify={"space-between"} className={classes.container}>
        <Grid item xs={8}>
          <Grid container spacing={isMobile ? 3 : 10}>
            <Grid item xs={isMobile ? 12 : "auto"}>
              <Typography variant={"h6"} className={classes.h6}>
                <strong>
                  <FormattedMessage id={"FOOTER_PRODUCT"} />
                </strong>
              </Typography>
              <List>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={Link}
                  to={Locale.localeUrl("/service")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_product_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_AB_TESTING"} />
                </ListItem>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={Link}
                  to={Locale.localeUrl("/service/feature-flags")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_feature_flag_click` })
                  }}>
                  <FormattedMessage id={"feature_flag"} />
                </ListItem>
                {shownDataAnalyticsMenu && (
                  <ListItem
                    disableGutters
                    className={classes.footerAlpha6Text}
                    component={Link}
                    to={Locale.localeUrl("/service/analytics")}
                    onClick={() => {
                      track({ key: `homepage_${pageName}_footer_product_click` })
                    }}>
                    <FormattedMessage id={"data_analytics"} />
                  </ListItem>
                )}
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={Link}
                  to={Locale.localeUrl("/service/funnel")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_funnel_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_FUNNEL"} />
                </ListItem>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={Link}
                  to={Locale.localeUrl("/pricing")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_pricing_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_PRICING"} />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={isMobile ? 12 : "auto"}>
              <Typography variant={"h6"} className={classes.h6}>
                <strong>
                  <FormattedMessage id={"SERVICE_GUIDE"} />
                </strong>
              </Typography>
              <List>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={getDocsUrl("sdk_guide")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_sdk_document_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_SDK"} />
                </ListItem>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={getDocsUrl("user_guide")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_user_guide_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_USER_MANUAL"} />
                </ListItem>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={getDocsUrl("faq")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_faq_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_FAQ"} />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={isMobile ? 12 : "auto"}>
              <Typography variant={"h6"} className={classes.h6}>
                <strong>
                  <FormattedMessage id={"FOOTER_COMPANY"} />
                </strong>
              </Typography>
              <List>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={getDocsUrl("about_company")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_company_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_ABOUT"} />
                </ListItem>
                {lang === "ko" && (
                  <ListItem
                    disableGutters
                    className={classes.footerAlpha6Text}
                    component={"a"}
                    target={"_blank"}
                    href={getDocsUrl("career")}
                    onClick={() => {
                      track({ key: `homepage_${pageName}_footer_hire_click` })
                    }}>
                    <FormattedMessage id={"FOOTER_CAREERS"} />
                  </ListItem>
                )}
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={getDocsUrl("blog")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_blog_click` })
                  }}>
                  <FormattedMessage id={"FOOTER_BLOG"} />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={isMobile ? 12 : "auto"}>
              <Typography variant={"h6"} className={classes.h6}>
                <strong>
                  <FormattedMessage id={"FOOTER_SUPPORT"} />
                </strong>
              </Typography>
              <List>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={getDocsUrl("slack")}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_slack_click` })
                  }}>
                  Slack
                </ListItem>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  href={"mailto:support@hackle.io"}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_email_click` })
                  }}>
                  Email
                </ListItem>
                <ListItem
                  disableGutters
                  className={classes.footerAlpha6Text}
                  component={"a"}
                  target={"_blank"}
                  href={"https://status.hackle.io"}
                  onClick={() => {
                    track({ key: `homepage_${pageName}_footer_status_click` })
                  }}>
                  Status
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={isMobile ? 12 : "auto"}>
          <Grid container spacing={3}>
            <Grid item>
              <a
                target={"_blank"}
                rel="noopener noreferrer"
                href={"https://medium.com/hackle"}
                onClick={() => {
                  track({ key: `homepage_${pageName}_footer_sns_medium_click` })
                }}>
                <img src={Medium} width={"32px"} alt="medium" />
              </a>
            </Grid>
            <Grid item>
              <a
                target={"_blank"}
                rel="noopener noreferrer"
                href={"https://www.linkedin.com/company/hacklesoftware"}
                onClick={() => {
                  track({ key: `homepage_${pageName}_footer_sns_linkedin_click` })
                }}>
                <img src={LinkedIn} width={"32px"} alt="linkedin" />
              </a>
            </Grid>
            <Grid item>
              <a
                target={"_blank"}
                rel="noopener noreferrer"
                href={"https://www.facebook.com/hacklesoftware/"}
                onClick={() => {
                  track({ key: `homepage_${pageName}_footer_sns_facebook_click` })
                }}>
                <img src={Facebook} width={"32px"} alt="facebook" />
              </a>
            </Grid>
            <Grid item>
              <a
                target={"_blank"}
                rel="noopener noreferrer"
                href={"https://www.youtube.com/channel/UCVFrSoFBj3GM-jwe8KOPzgA"}
                onClick={() => {
                  track({ key: `homepage_${pageName}_footer_sns_youtube_click` })
                }}>
                <img src={Youtube} width={"32px"} alt="youtube" />
              </a>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ marginTop: isDesktop ? "155px" : "20px" }}>
          <Grid container spacing={1}>
            {lang === "ko" ? (
              <CompanyInfo isMobile={isMobile} />
            ) : (
              <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
                Hackle Ⓒ
              </Grid>
            )}
            <Grid item className={classes.footerDivider}>
              |
            </Grid>
            <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
              <a
                target={"_blank"}
                rel="noopener noreferrer"
                href={getDocsUrl("policy_privacy")}
                onClick={() => {
                  track({ key: `homepage_${pageName}_footer_privacy_policy_click` })
                }}>
                <FormattedMessage id={"POLICY_PRIVACY"} />
              </a>
            </Grid>
            <Grid item className={classes.footerDivider}>
              |
            </Grid>
            <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
              <a
                target={"_blank"}
                rel="noopener noreferrer"
                href={getDocsUrl("policy_terms_of_service")}
                onClick={() => {
                  track({ key: `homepage_${pageName}_footer_terms_of_service_click` })
                }}>
                <FormattedMessage id={"POLICY_TERMS_OF_SERVICE"} />
              </a>
            </Grid>
            {lang === "ko" &&
                <>
                  <Grid item className={classes.footerDivider}>
                    |
                  </Grid>
                  <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
                    <a
                      target={"_blank"}
                      rel="noopener noreferrer"
                      href={"https://hackle-tnc.notion.site/hackle-tnc/325dc362cc7e4278a0864240c064d1fa"}
                      onClick={() => {
                        track({ key: `homepage_${pageName}_footer_notice_click` })
                      }}>
                      공지사항
                    </a>
                  </Grid>
                </>
            }
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

interface CompanyInfoProps {
  isMobile: boolean
}

function CompanyInfo({ isMobile }: CompanyInfoProps) {
  const { lang } = Locale.getDefault()
  const classes = useStyles({ lang })

  return (
    <>
      <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
        핵클 주식회사
      </Grid>
      <Grid item className={classes.footerDivider}>
        |
      </Grid>
      <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
        대표자 : 선우창학
      </Grid>
      <Grid item className={classes.footerDivider}>
        |
      </Grid>
      <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
        서울특별시 강남구 논현로75길 8, 5층
      </Grid>
      <Grid item className={classes.footerDivider}>
        |
      </Grid>
      <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
        사업자 등록 번호 : 680-81-01653
      </Grid>
      <Grid item className={classes.footerDivider}>
        |
      </Grid>
      <Grid item xs={isMobile ? 12 : "auto"} className={classes.footerAlpha3Text}>
        연락처 : 070-8018-2806
      </Grid>
    </>
  )
}
